import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { User, LogOut, Save, Globe, Instagram, Upload, Camera, Shield, AlertCircle, CheckCircle, Clock, Image } from 'lucide-react';
import { PayPalWrapper as PayPalCheckout } from '../components/PayPalWrapper';
import ArtGallery from '../components/ArtGallery';

const API_URL = "";

export const MemberProfile = () => {
  const { user, token, logout, updateProfile, isAuthenticated, loading: authLoading } = useAuth();
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [membershipStatus, setMembershipStatus] = useState(null);
  const [showRenewalPayPal, setShowRenewalPayPal] = useState(false);
  const [selectedRenewalType, setSelectedRenewalType] = useState('annual');
  const [gallery, setGallery] = useState([]);
  const [galleryLoading, setGalleryLoading] = useState(true);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    home_phone: '',
    mobile_phone: '',
    website: '',
    instagram: '',
    position: '',
    photo_url: ''
  });
  const navigate = useNavigate();
  
  const isAdmin = user?.access && ['Administrator', 'Webmaster', 'Admin'].includes(user.access);
  const canReviewGallery = isAdmin || (user?.gallery_roles || []).some((role) => ['reviewer', 'coordinator', 'publisher'].includes(role));

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/members/login');
    }
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    if (user) {
      setFormData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        address: user.address || '',
        city: user.city || '',
        state: user.state || '',
        zip_code: user.zip_code || '',
        home_phone: user.home_phone || '',
        mobile_phone: user.mobile_phone || '',
        website: user.website || '',
        instagram: user.instagram || '',
        position: user.position || '',
        photo_url: user.photo_url || ''
      });
      
      // Fetch membership status
      fetchMembershipStatus();
    }
  }, [user]);

  const fetchMembershipStatus = async () => {
    if (!token) return;
    
    try {
      const response = await fetch(`${API_URL}/api/membership/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const status = await response.json();
        setMembershipStatus(status);
      }
    } catch (error) {
      console.error('Error fetching membership status:', error);
    }
  };

  const fetchGallery = async () => {
    if (!token) return;
    
    setGalleryLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/members/gallery`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setGallery(data.gallery || []);
      }
    } catch (error) {
      console.error('Error fetching gallery:', error);
    } finally {
      setGalleryLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchGallery();
    }
  }, [token]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload an image file (JPEG, PNG, GIF, or WebP)');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    setUploading(true);
    const uploadData = new FormData();
    uploadData.append('file', file);

    try {
      const response = await fetch(`${API_URL}/api/members/upload-photo`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: uploadData
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Upload failed');
      }

      const data = await response.json();
      setFormData({ ...formData, photo_url: data.photo_url });
      toast.success('Photo uploaded successfully!');
      
      // Refresh user data
      await updateProfile({ photo_url: data.photo_url });
    } catch (error) {
      toast.error(error.message || 'Failed to upload photo');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await updateProfile(formData);
      toast.success('Profile updated successfully!');
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Renewal functions
  const getRenewalPrice = () => selectedRenewalType === 'lifetime' ? 500 : 60;
  
  const getRenewalItems = () => [{
    name: selectedRenewalType === 'lifetime' ? 'Lifetime Membership' : 'Annual Membership Renewal',
    description: `Coppini Academy of Fine Art - ${selectedRenewalType === 'lifetime' ? 'One-time payment' : 'Annual'}`,
    quantity: 1,
    price: getRenewalPrice(),
    category: 'membership'
  }];

  const handleRenewalSuccess = async (result) => {
    // Call the renew endpoint to update membership
    try {
      const response = await fetch(`${API_URL}/api/membership/renew`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ membership_type: selectedRenewalType })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message || 'Membership renewed successfully!');
        setShowRenewalPayPal(false);
        fetchMembershipStatus();
      } else {
        toast.error('Failed to process renewal');
      }
    } catch (error) {
      toast.error('Error processing renewal');
    }
  };

  const handleRenewalError = (error) => {
    toast.error('Payment failed. Please try again.');
    console.error('Renewal payment error:', error);
  };

  // Format expiration date
  const formatExpirationDate = (dateStr) => {
    if (!dateStr) return null;
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  // Get status badge color
  const getStatusBadge = () => {
    if (!membershipStatus) return null;
    
    if (membershipStatus.is_lifetime) {
      return { color: 'bg-cafa-red-100 text-cafa-red-900', text: 'Lifetime Member', icon: CheckCircle };
    }
    
    if (membershipStatus.in_grace_period) {
      return { color: 'bg-red-100 text-red-800', text: 'Expired - Grace Period', icon: AlertCircle };
    }
    
    if (membershipStatus.days_until_expiry !== null && membershipStatus.days_until_expiry <= 0) {
      return { color: 'bg-red-100 text-red-800', text: 'Expired', icon: AlertCircle };
    }
    
    if (membershipStatus.days_until_expiry !== null && membershipStatus.days_until_expiry <= 30) {
      return { color: 'bg-yellow-100 text-yellow-800', text: 'Expiring Soon', icon: Clock };
    }
    
    if (membershipStatus.is_active) {
      return { color: 'bg-green-100 text-green-800', text: 'Active', icon: CheckCircle };
    }
    
    return { color: 'bg-stone-100 text-stone-800', text: 'Inactive', icon: AlertCircle };
  };

  // Helper to get full photo URL
  const getPhotoUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    return `${API_URL}${url}`;
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cafa-red"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="member-profile-page">
      {/* Header */}
      <section className="bg-gradient-to-b from-stone-50 to-white py-8 border-b border-stone-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <h1 className="text-3xl font-serif text-stone-900">Member Area</h1>
              <p className="text-stone-600 mt-1">Welcome, {user?.first_name}!</p>
            </div>
            
            <div className="flex flex-wrap items-center gap-3">
              {isAdmin && (
                <Link to="/admin/content">
                  <Button variant="ghost" className="text-cafa-red">
                    <Shield size={16} className="mr-1" /> Admin
                  </Button>
                </Link>
              )}
              <Link to="/members/directory">
                <Button variant="ghost" className="text-stone-600">
                  Member Directory
                </Button>
              </Link>
              <Link to="/members/profile">
                <Button variant="ghost" className="text-cafa-red">
                  My Profile
                </Button>
              </Link>
              <Link to="/members/history">
                <Button variant="ghost" className="text-stone-600">
                  My History
                </Button>
              </Link>
              <Link to="/members/gallery" data-testid="member-profile-gallery-link">
                <Button variant="ghost" className="text-stone-600">Gallery Applications</Button>
              </Link>
              {canReviewGallery && (
                <Button asChild className="min-h-[48px] text-base bg-[#7c2434] text-white hover:bg-[#651d2a]">
                  <Link to="/admin/gallery" data-testid="member-profile-gallery-staff-link">Gallery Staff — Review member artwork</Link>
                </Button>
              )}
              <Button variant="ghost" onClick={handleLogout} className="text-stone-600">
                <LogOut size={18} className="mr-1" /> Logout
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Profile Form */}
      <section className="py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="border-stone-200">
            <CardHeader>
              <CardTitle className="text-2xl font-serif">My Profile</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Photo */}
                <div className="pb-6 border-b border-stone-200">
                  <div className="flex items-start gap-6">
                    {/* Photo Preview */}
                    <div className="relative group">
                      {formData.photo_url ? (
                        <img
                          src={getPhotoUrl(formData.photo_url)}
                          alt="Profile"
                          className="w-28 h-28 rounded-full object-cover border-2 border-stone-200"
                        />
                      ) : (
                        <div className="w-28 h-28 rounded-full bg-cafa-red-100 flex items-center justify-center border-2 border-stone-200">
                          <User size={48} className="text-cafa-red" />
                        </div>
                      )}
                      {/* Upload overlay on hover */}
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                        className="absolute inset-0 rounded-full bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer"
                      >
                        {uploading ? (
                          <div className="animate-spin rounded-full h-6 w-6 border-2 border-white border-t-transparent" />
                        ) : (
                          <Camera size={24} className="text-white" />
                        )}
                      </button>
                    </div>

                    {/* Upload & URL Options */}
                    <div className="flex-1 space-y-4">
                      {/* Upload Button */}
                      <div>
                        <Label className="text-stone-700 font-medium">Upload Photo</Label>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/jpeg,image/png,image/gif,image/webp"
                          onChange={handlePhotoUpload}
                          className="hidden"
                          data-testid="photo-upload-input"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={uploading}
                          className="mt-2 w-full sm:w-auto border-cafa-red text-cafa-red hover:bg-cafa-red-50"
                          data-testid="photo-upload-button"
                        >
                          {uploading ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-2 border-cafa-red border-t-transparent mr-2" />
                              Uploading...
                            </>
                          ) : (
                            <>
                              <Upload size={16} className="mr-2" />
                              Choose Photo
                            </>
                          )}
                        </Button>
                        <p className="text-xs text-stone-500 mt-1">JPEG, PNG, GIF or WebP. Max 5MB.</p>
                      </div>

                      {/* OR Divider */}
                      <div className="flex items-center gap-3">
                        <div className="flex-1 border-t border-stone-200"></div>
                        <span className="text-xs text-stone-400 uppercase">or</span>
                        <div className="flex-1 border-t border-stone-200"></div>
                      </div>

                      {/* Photo URL Input */}
                      <div>
                        <Label htmlFor="photo_url" className="text-stone-700">Photo URL</Label>
                        <Input
                          id="photo_url"
                          name="photo_url"
                          value={formData.photo_url}
                          onChange={handleChange}
                          placeholder="https://example.com/photo.jpg"
                          className="mt-2"
                          data-testid="photo-url-input"
                        />
                        <p className="text-xs text-stone-500 mt-1">Or paste a link to your photo</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Name */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="first_name">First Name</Label>
                    <Input
                      id="first_name"
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleChange}
                      className="mt-2"
                      data-testid="profile-firstname"
                    />
                  </div>
                  <div>
                    <Label htmlFor="last_name">Last Name</Label>
                    <Input
                      id="last_name"
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleChange}
                      className="mt-2"
                      data-testid="profile-lastname"
                    />
                  </div>
                </div>

                {/* Position */}
                <div>
                  <Label htmlFor="position">Position/Title</Label>
                  <Input
                    id="position"
                    name="position"
                    value={formData.position}
                    onChange={handleChange}
                    placeholder="e.g., Artist, Instructor, Board Member"
                    className="mt-2"
                  />
                </div>

                {/* Social Links */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="website" className="flex items-center gap-2">
                      <Globe size={16} /> Website
                    </Label>
                    <Input
                      id="website"
                      name="website"
                      value={formData.website}
                      onChange={handleChange}
                      placeholder="https://yourwebsite.com"
                      className="mt-2"
                      data-testid="profile-website"
                    />
                  </div>
                  <div>
                    <Label htmlFor="instagram" className="flex items-center gap-2">
                      <Instagram size={16} /> Instagram
                    </Label>
                    <Input
                      id="instagram"
                      name="instagram"
                      value={formData.instagram}
                      onChange={handleChange}
                      placeholder="@yourusername"
                      className="mt-2"
                      data-testid="profile-instagram"
                    />
                  </div>
                </div>

                {/* Address */}
                <div className="pt-4 border-t border-stone-200">
                  <h3 className="text-lg font-medium text-stone-900 mb-4">Contact Information</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="address">Street Address</Label>
                      <Input
                        id="address"
                        name="address"
                        value={formData.address}
                        onChange={handleChange}
                        className="mt-2"
                      />
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="col-span-2">
                        <Label htmlFor="city">City</Label>
                        <Input
                          id="city"
                          name="city"
                          value={formData.city}
                          onChange={handleChange}
                          className="mt-2"
                        />
                      </div>
                      <div>
                        <Label htmlFor="state">State</Label>
                        <Input
                          id="state"
                          name="state"
                          value={formData.state}
                          onChange={handleChange}
                          className="mt-2"
                        />
                      </div>
                      <div>
                        <Label htmlFor="zip_code">ZIP Code</Label>
                        <Input
                          id="zip_code"
                          name="zip_code"
                          value={formData.zip_code}
                          onChange={handleChange}
                          className="mt-2"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="home_phone">Home Phone</Label>
                        <Input
                          id="home_phone"
                          name="home_phone"
                          value={formData.home_phone}
                          onChange={handleChange}
                          className="mt-2"
                        />
                      </div>
                      <div>
                        <Label htmlFor="mobile_phone">Mobile Phone</Label>
                        <Input
                          id="mobile_phone"
                          name="mobile_phone"
                          value={formData.mobile_phone}
                          onChange={handleChange}
                          className="mt-2"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Membership Status */}
                <div className="pt-4 border-t border-stone-200">
                  <h3 className="text-lg font-medium text-stone-900 mb-4">Membership Status</h3>
                  
                  {membershipStatus?.is_lifetime ? (
                    // Lifetime member - no renewal needed
                    <div className="bg-cafa-red-50 border border-cafa-blue-200 rounded-lg p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-cafa-red-100 rounded-full flex items-center justify-center">
                          <CheckCircle className="text-cafa-red" size={24} />
                        </div>
                        <div>
                          <p className="font-semibold text-stone-900">Lifetime Member</p>
                          <p className="text-sm text-stone-600">Thank you for your continued support!</p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    // Regular member - show status and renewal option
                    <div className="space-y-4">
                      {/* Status Card */}
                      <div className={`rounded-lg p-4 ${
                        membershipStatus?.in_grace_period || (membershipStatus?.days_until_expiry !== null && membershipStatus?.days_until_expiry <= 0)
                          ? 'bg-red-50 border border-red-200'
                          : membershipStatus?.days_until_expiry !== null && membershipStatus?.days_until_expiry <= 30
                          ? 'bg-yellow-50 border border-yellow-200'
                          : 'bg-stone-50 border border-stone-200'
                      }`}>
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-3">
                            {getStatusBadge() && (
                              <>
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                  membershipStatus?.is_active ? 'bg-green-100' : 'bg-red-100'
                                }`}>
                                  {React.createElement(getStatusBadge().icon, {
                                    className: membershipStatus?.is_active ? 'text-green-700' : 'text-red-700',
                                    size: 24
                                  })}
                                </div>
                                <div>
                                  <div className="flex items-center gap-2">
                                    <p className="font-semibold text-stone-900">
                                      {membershipStatus?.is_active ? 'Active Member' : 'Membership Expired'}
                                    </p>
                                    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getStatusBadge().color}`}>
                                      {getStatusBadge().text}
                                    </span>
                                  </div>
                                  {membershipStatus?.expires_at && (
                                    <p className="text-sm text-stone-600 mt-1">
                                      {membershipStatus.days_until_expiry > 0 
                                        ? `Expires: ${formatExpirationDate(membershipStatus.expires_at)} (${membershipStatus.days_until_expiry} days)`
                                        : membershipStatus.in_grace_period
                                        ? `Expired - Grace period ends ${formatExpirationDate(membershipStatus.grace_period_ends)}`
                                        : `Expired on ${formatExpirationDate(membershipStatus.expires_at)}`
                                      }
                                    </p>
                                  )}
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                        
                        {/* Renewal Warning */}
                        {membershipStatus?.days_until_expiry !== null && membershipStatus?.days_until_expiry <= 30 && (
                          <div className={`mt-4 p-3 rounded ${
                            membershipStatus.days_until_expiry <= 0 ? 'bg-red-100' : 'bg-yellow-100'
                          }`}>
                            <p className={`text-sm font-medium ${
                              membershipStatus.days_until_expiry <= 0 ? 'text-red-800' : 'text-yellow-800'
                            }`}>
                              {membershipStatus.days_until_expiry <= 0 
                                ? membershipStatus.in_grace_period
                                  ? 'Your membership has expired! Renew now to keep your benefits.'
                                  : 'Your membership has expired. Please renew to regain access.'
                                : `Your membership expires soon. Renew now to avoid interruption.`
                              }
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Renewal Section */}
                      {!showRenewalPayPal ? (
                        <div className="bg-white border border-stone-200 rounded-lg p-4">
                          <h4 className="font-medium text-stone-900 mb-3">Renew Membership</h4>
                          
                          <div className="space-y-3 mb-4">
                            <label className={`flex items-center justify-between p-3 border-2 rounded-lg cursor-pointer transition-colors ${
                              selectedRenewalType === 'annual' ? 'border-cafa-red bg-cafa-red-50' : 'border-stone-200 hover:bg-stone-50'
                            }`}>
                              <div className="flex items-center gap-3">
                                <input
                                  type="radio"
                                  name="renewalType"
                                  value="annual"
                                  checked={selectedRenewalType === 'annual'}
                                  onChange={(e) => setSelectedRenewalType(e.target.value)}
                                  className="accent-cafa-red"
                                />
                                <div>
                                  <p className="font-medium text-stone-900">Annual Membership</p>
                                  <p className="text-xs text-stone-500">Renew for one year</p>
                                </div>
                              </div>
                              <span className="text-xl font-bold text-cafa-red">$60</span>
                            </label>
                            
                            <label className={`flex items-center justify-between p-3 border-2 rounded-lg cursor-pointer transition-colors ${
                              selectedRenewalType === 'lifetime' ? 'border-cafa-red bg-cafa-red-50' : 'border-stone-200 hover:bg-stone-50'
                            }`}>
                              <div className="flex items-center gap-3">
                                <input
                                  type="radio"
                                  name="renewalType"
                                  value="lifetime"
                                  checked={selectedRenewalType === 'lifetime'}
                                  onChange={(e) => setSelectedRenewalType(e.target.value)}
                                  className="accent-cafa-red"
                                />
                                <div>
                                  <p className="font-medium text-stone-900">Lifetime Membership</p>
                                  <p className="text-xs text-stone-500">One-time payment, never renew again</p>
                                </div>
                              </div>
                              <span className="text-xl font-bold text-cafa-red">$500</span>
                            </label>
                          </div>
                          
                          <Button 
                            onClick={() => setShowRenewalPayPal(true)}
                            className="w-full bg-cafa-red hover:bg-cafa-red-900 text-white"
                            data-testid="renew-membership-btn"
                          >
                            Proceed to Payment - ${getRenewalPrice()}
                          </Button>
                        </div>
                      ) : (
                        <div className="bg-white border border-stone-200 rounded-lg p-4">
                          <h4 className="font-medium text-stone-900 mb-3">
                            Complete Payment - {selectedRenewalType === 'lifetime' ? 'Lifetime' : 'Annual'} Membership (${getRenewalPrice()})
                          </h4>
                          
                          <PayPalCheckout
                            items={getRenewalItems()}
                            payerEmail={user?.email}
                            payerName={`${user?.first_name || ''} ${user?.last_name || ''}`.trim()}
                            authToken={token}
                            onSuccess={handleRenewalSuccess}
                            onError={handleRenewalError}
                            onCancel={() => setShowRenewalPayPal(false)}
                            registrationData={{
                              type: 'membership',
                              membership_tier: selectedRenewalType === 'lifetime' ? 'Lifetime Membership' : 'Annual Membership',
                              membership_price: getRenewalPrice()
                            }}
                          />
                          
                          <Button 
                            variant="outline"
                            onClick={() => setShowRenewalPayPal(false)}
                            className="w-full mt-3"
                          >
                            Cancel
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Save Button */}
                <div className="pt-6">
                  <Button
                    type="submit"
                    className="w-full bg-cafa-red hover:bg-cafa-red-900 text-white"
                    disabled={saving}
                    data-testid="profile-save"
                  >
                    <Save size={18} className="mr-2" />
                    {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>

          {/* Art Gallery Section */}
          <Card className="border-stone-200 mt-8">
            <CardHeader>
              <CardTitle className="text-2xl font-serif flex items-center gap-2">
                <Image size={24} className="text-cafa-red" />
                My Art Gallery
              </CardTitle>
              <p className="text-sm text-stone-500 mt-1">
                Showcase your artwork to other members. Upload up to 12 images.
              </p>
            </CardHeader>
            <CardContent>
              {galleryLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cafa-red"></div>
                </div>
              ) : (
                <ArtGallery 
                  gallery={gallery} 
                  maxImages={12} 
                  token={token}
                  onUpdate={fetchGallery}
                />
              )}
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};
